import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PhoneField } from "../../PhoneField/PhoneField";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux/useRedux";
import { selectLocationCode } from "../../../redux/slices/profileSlice/ProfileSlice";
import {
  getUserViewThunk,
  getSocialProfileThunk,
} from "../../../redux/slices/profileSlice/asyncthunk";
import {
  postConnectWhatsAppCheck,
  postConnectWhatsAppPhone,
} from "../../../api/connections/connectService";

export interface ConnectWhatsAppModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface WhatsAppResponseData {
  instance_id?: number;
  qr_base64?: string;
  pairing_code?: string;
  message?: string;
  [key: string]: any;
}

/* WhatsApp Icon SVG matching mobile WhatsappSettingsIcon */
function WhatsAppIcon({ size = 32 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 50 50"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="shrink-0"
    >
      <rect width="50" height="50" rx="25" fill="#60D669" />
      <path
        d="M11.442 24.877c0 2.388.626 4.72 1.818 6.776l-1.932 7.023 7.22-1.884a13.66 13.66 0 006.508 1.65h.006c7.505 0 13.615-6.08 13.618-13.554a13.439 13.439 0 00-3.985-9.589 13.563 13.563 0 00-9.634-3.975c-7.506 0-13.616 6.08-13.619 13.553"
        fill="url(#wa_grad1)"
      />
      <path
        d="M10.956 24.872c0 2.475.648 4.89 1.883 7.02l-2.002 7.275 7.479-1.953a14.151 14.151 0 006.741 1.71h.006c7.775 0 14.104-6.3 14.108-14.04a13.923 13.923 0 00-4.129-9.933 14.05 14.05 0 00-9.979-4.118c-7.776 0-14.104 6.298-14.107 14.04zm4.454 6.653l-.28-.441a11.6 11.6 0 01-1.792-6.21c.002-6.435 5.262-11.67 11.73-11.67a11.676 11.676 0 018.29 3.423 11.573 11.573 0 013.43 8.256c-.002 6.434-5.262 11.67-11.725 11.67h-.004a11.752 11.752 0 01-5.968-1.628l-.428-.252-4.438 1.158 1.185-4.306z"
        fill="url(#wa_grad2)"
      />
      <path
        d="M21.538 19.003c-.264-.584-.542-.596-.794-.606-.205-.009-.44-.008-.675-.008-.235 0-.617.088-.94.439-.323.35-1.234 1.2-1.234 2.926 0 1.727 1.263 3.395 1.44 3.63.176.234 2.438 3.89 6.02 5.296 2.979 1.17 3.584.937 4.23.878.647-.058 2.086-.848 2.38-1.668.294-.819.294-1.521.206-1.668-.088-.146-.323-.234-.676-.41-.353-.175-2.086-1.024-2.409-1.141-.323-.117-.558-.176-.793.176-.235.35-.91 1.14-1.116 1.375-.205.234-.411.264-.763.088-.353-.176-1.488-.546-2.835-1.741-1.048-.93-1.756-2.08-1.961-2.43-.206-.351-.022-.542.155-.716.158-.158.352-.41.529-.615.175-.205.234-.351.352-.585.117-.235.058-.44-.03-.615s-.773-1.911-1.086-2.605z"
        fill="#ffffff"
      />
      <defs>
        <linearGradient
          id="wa_grad1"
          x1="1378.93"
          y1="2746.57"
          x2="1378.93"
          y2="11.324"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#1FAF38" />
          <stop offset="1" stopColor="#60D669" />
        </linearGradient>
        <linearGradient
          id="wa_grad2"
          x1="1427.5"
          y1="2844.17"
          x2="1427.5"
          y2="10.8333"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#F9F9F9" />
          <stop offset="1" stopColor="#ffffff" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/* Copy Icon SVG */
function CopyIcon({ className = "w-4 h-4 text-[#302B2B]" }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 20 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.54 18.843a2.13 2.13 0 01-1.933 1.948c-1.039.107-2.59.209-4.75.209s-3.71-.102-4.75-.21a2.13 2.13 0 01-1.932-1.947c-.113-1.16-.223-2.988-.223-5.7s.11-4.54.223-5.7a2.13 2.13 0 011.932-1.948"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.71 1.019c.291.085 1.437.505 3.125 2.187 1.58 1.573 2.05 2.674 2.174 3.046.024.76.039 1.625.039 2.605 0 2.712-.11 4.54-.223 5.7a2.13 2.13 0 01-1.932 1.948c-1.04.107-2.59.21-4.75.21s-3.711-.103-4.75-.21a2.13 2.13 0 01-1.933-1.948c-.112-1.16-.222-2.988-.222-5.7s.11-4.54.222-5.7A2.13 2.13 0 017.393 1.21c1.039-.108 2.59-.21 4.75-.21.563 0 1.085.007 1.567.02z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 6.399c-.795.121-2.18.043-3.31-.057a2.203 2.203 0 01-2.007-2.004c-.1-1.133-.178-2.521-.053-3.31"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* Error Icon SVG */
function ErrorIcon() {
  return (
    <svg
      className="w-4 h-4 text-[#DE2424] shrink-0"
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/* Close Icon SVG */
function CloseIcon() {
  return (
    <svg
      className="w-5 h-5 text-[#302B2B]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export const ConnectWhatsAppModal: React.FC<ConnectWhatsAppModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const locationCode = useAppSelector(selectLocationCode);

  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [fullPhoneNumber, setFullPhoneNumber] = useState<string>("");
  const [selectedCountry, setSelectedCountry] = useState<string>(() => {
    return String(locationCode?.country_code || "US").toUpperCase();
  });
  const [isPhoneValid, setIsPhoneValid] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [connectStep, setConnectStep] = useState<"phone" | "check">("phone");
  const [copied, setCopied] = useState<boolean>(false);
  const [data, setData] = useState<WhatsAppResponseData | null>(null);

  const handleClose = () => {
    setPhoneNumber("");
    setFullPhoneNumber("");
    setIsPhoneValid(false);
    setError(null);
    setConnectStep("phone");
    setData(null);
    setLoading(false);
    setCopied(false);
    onClose();
  };

  // Reset when opened
  useEffect(() => {
    if (isOpen) {
      setPhoneNumber("");
      setFullPhoneNumber("");
      setIsPhoneValid(false);
      setError(null);
      setConnectStep("phone");
      setData(null);
      setLoading(false);
      setCopied(false);
      if (locationCode?.country_code) {
        setSelectedCountry(String(locationCode.country_code).toUpperCase());
      }
    }
  }, [isOpen, locationCode?.country_code]);

  // Handle escape key
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen]);

  const onConnect = async () => {
    setError(null);

    if (!isPhoneValid || !phoneNumber.trim()) {
      setError(
        t("connectWhatsApp.invalidPhone", "Please enter a valid phone number"),
      );
      return;
    }

    setLoading(true);

    const formatted = (fullPhoneNumber || phoneNumber).replace(/\s+/g, "");

    try {
      const res = await postConnectWhatsAppPhone(formatted);
      setError(null);
      setData(res.data);
      setConnectStep("check");
    } catch (err: any) {
      const errMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        t(
          "connectWhatsApp.errorDefault",
          "Failed to connect WhatsApp. Please try again.",
        );
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const onCopy = async () => {
    if (!data?.pairing_code) return;
    try {
      await navigator.clipboard.writeText(data.pairing_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Failed to copy pairing code", e);
    }
  };

  const onCheck = async () => {
    if (!data?.instance_id) return;
    setError(null);
    setLoading(true);

    try {
      const res = await postConnectWhatsAppCheck(data.instance_id);
      setError(null);
      if (res.status === 200) {
        dispatch(getUserViewThunk());
        handleClose();
        setTimeout(() => {
          dispatch(getSocialProfileThunk());
          onSuccess?.();
        }, 500);
      } else {
        setData(res.data);
      }
    } catch (err: any) {
      const errMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        t("connectWhatsApp.errorDefault", "Failed to verify connection. Please try again.");
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !loading) {
      e.preventDefault();
      onConnect();
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={handleClose}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        />

        {/* Modal Window */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 16 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className={`relative w-full max-w-[440px] bg-[#EDEEF3] rounded-[28px] shadow-2xl z-10 flex flex-col max-h-[92vh] ${
            connectStep === "phone" ? "overflow-visible" : "overflow-hidden"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* STEP 1: Phone Input View */}
          {connectStep === "phone" && (
            <div className="p-6 md:p-7 flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <WhatsAppIcon size={34} />
                  <h3 className="text-lg font-bold text-[#302B2B]">
                    {t("connectWhatsApp.title", "Connect WhatsApp")}
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-2 rounded-xl hover:bg-black/5 transition-colors cursor-pointer"
                  aria-label="Close"
                >
                  <CloseIcon />
                </button>
              </div>

              {/* Phone Input Box */}
              <div className="flex flex-col gap-2">
                <PhoneField
                  phone={phoneNumber}
                  country={selectedCountry}
                  label={false}
                  containerHeight="h-[54px]"
                  error={error}
                  required={false}
                  placeholder={t(
                    "connectWhatsApp.phonePlaceholder",
                    "Type your phone number",
                  )}
                  onPhoneChange={(val: any, valid: boolean, e164: string) => {
                    setPhoneNumber(val);
                    setIsPhoneValid(valid);
                    setFullPhoneNumber(e164);
                    if (error) setError(null);
                  }}
                  onCountryChange={(c: string) => {
                    setSelectedCountry(c);
                    if (error) setError(null);
                  }}
                  onKeyDown={handlePhoneKeyDown}
                />
              </div>

              {/* Submit Button */}
              <button
                type="button"
                onClick={onConnect}
                disabled={loading}
                className="w-full mt-4 h-[50px] bg-[#0b0e39] hover:bg-[#161a52] text-white font-semibold rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>{t("connectWhatsApp.loading", "Loading...")}</span>
                  </>
                ) : (
                  <span>{t("connectWhatsApp.connect", "Connect")}</span>
                )}
              </button>
            </div>
          )}

          {/* STEP 2: QR & Pairing Code Check View */}
          {connectStep === "check" && (
            <div className="flex flex-col flex-1 overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-gray-200/60 shrink-0">
                <div className="flex items-center gap-2.5">
                  <WhatsAppIcon size={30} />
                  <span className="text-base font-bold text-[#302B2B]">
                    {t("connectWhatsApp.scanQrCode", "Scan QR Code")}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleClose}
                  className="p-1.5 rounded-xl hover:bg-black/5 transition-colors"
                  aria-label="Close"
                >
                  <CloseIcon />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto px-6 py-4 flex flex-col items-center gap-3.5">
                {/* QR Code Image */}
                {data?.qr_base64 && (
                  <div className="bg-white p-3 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center">
                    <img
                      src={
                        data.qr_base64.startsWith("data:")
                          ? data.qr_base64
                          : `data:image/png;base64,${data.qr_base64}`
                      }
                      alt="WhatsApp QR Code"
                      className="w-48 h-48 sm:w-52 sm:h-52 object-contain"
                    />
                  </div>
                )}

                {/* "Or use pairing code:" */}
                <div className="w-full text-center">
                  <span className="text-sm font-normal text-[#9A99AB]">
                    {t("connectWhatsApp.orPairingCode", "Or use pairing code:")}
                  </span>

                  {/* Pairing Code Button */}
                  {data?.pairing_code && (
                    <button
                      type="button"
                      onClick={onCopy}
                      className="mt-2 inline-flex items-center justify-center gap-2 bg-[#F6F6F6] hover:bg-gray-200/80 px-4 py-2 rounded-xl transition-all duration-200 active:scale-95 border border-gray-200/50"
                    >
                      <span className="text-sm font-bold tracking-wider text-[#302B2B]">
                        {copied
                          ? t("connectWhatsApp.copied", "Copied!")
                          : data.pairing_code}
                      </span>
                      {copied ? (
                        <svg
                          className="w-4 h-4 text-emerald-600"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : (
                        <CopyIcon />
                      )}
                    </button>
                  )}
                </div>

                {/* Top message banner if provided */}
                {data?.message && (
                  <div className="w-full p-3 rounded-[15px] border border-[#eff6ff] bg-[#dbeafe] text-left">
                    <p className="text-xs text-[#0e1c7a] font-medium leading-relaxed whitespace-pre-line">
                      {data.message}
                    </p>
                  </div>
                )}

                {/* Instruction Steps */}
                <div className="w-full p-3.5 rounded-[15px] border border-[#f0fdf4] bg-[#dcfce7] text-left space-y-1">
                  <p className="text-xs text-[#166534] font-medium leading-relaxed">
                    {t("connectWhatsApp.step1", "1. Open WhatsApp on your phone")}
                  </p>
                  <p className="text-xs text-[#166534] font-medium leading-relaxed">
                    {t(
                      "connectWhatsApp.step2",
                      "2. Tap Menu or Settings and select Linked Devices",
                    )}
                  </p>
                  <p className="text-xs text-[#166534] font-medium leading-relaxed">
                    {t(
                      "connectWhatsApp.step3",
                      "3. Scan this QR code or enter the pairing code",
                    )}
                  </p>
                  <p className="text-xs text-[#166534] font-medium leading-relaxed">
                    {t(
                      "connectWhatsApp.step4",
                      "4. After scanning, the connection will be established",
                    )}
                  </p>
                </div>

                {/* Error Banner */}
                {error && (
                  <div className="w-full flex items-center gap-2 px-1">
                    <ErrorIcon />
                    <span className="text-xs font-bold text-[#DE2424]">
                      {error}
                    </span>
                  </div>
                )}
              </div>

              {/* Action Buttons Footer */}
              <div className="px-6 py-4 bg-[#EDEEF3] border-t border-gray-200/50 shrink-0 flex flex-col gap-2">
                <button
                  type="button"
                  onClick={onCheck}
                  disabled={loading}
                  className="w-full h-[48px] bg-[#0b0e39] hover:bg-[#161a52] text-white font-semibold rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>{t("connectWhatsApp.checking", "Checking...")}</span>
                    </>
                  ) : (
                    <span>
                      {t(
                        "connectWhatsApp.scannedOrEnteredCode",
                        "I've scanned or entered the code",
                      )}
                    </span>
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setConnectStep("phone");
                    setError(null);
                  }}
                  disabled={loading}
                  className="w-full py-2 text-xs font-semibold text-gray-500 hover:text-gray-800 transition"
                >
                  {t("common.back", "Change phone number")}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
